import React from 'react'
import { Icon } from '@lightspeed/design-system-react'

import { reactular } from '@vendhq/reactular'
import classNames from 'classnames'
import { PromotionDetails } from '../../../models/promotion'
import { LineItem } from '../../../models/line-item'
import {
  getVariantsDisplayNames,
  hasVariants,
  isPriceDiscounted,
} from '../../../functions/lineItem'
import { useAppSelector } from '../../../state/hooks'
import { selectLineItems } from '../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { LineItemTotalPrice } from './LineItemTotalPrice'
import { LineItemTotalBasePrice } from './LineItemTotalBasePrice'
import { LineItemCustomFields } from './LineItemCustomFields'

interface PromotionProps {
  promotionDetails: PromotionDetails[]
}

const LineItemPromotionDetails: React.FC<PromotionProps> = ({
  promotionDetails,
}) => (
  <>
    {promotionDetails.map((promotionDetail, i) => (
      <div
        key={i}
        className="vd-text-supplementary"
        data-cy="sale-summary-promo-data"
      >
        <div>
          <Icon icon="ticket" /> {promotionDetail.name}
        </div>
        {promotionDetail.promoCode && (
          <div className="li-cell-summary-promo-code">
            <Icon icon="key" /> {promotionDetail.promoCode}
          </div>
        )}
      </div>
    ))}
  </>
)

export const SaleSummaryLineItems: React.FC = () => {
  // Ensures we don't error out on the sale confirmation view as the sale manager is being initialised
  const lineItems = useAppSelector(selectLineItems) || []

  if (!lineItems.length) {
    return null
  }

  return (
    <ul className="sale-summary-list">
      {lineItems.map(lineItem => (
        <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
      ))}
    </ul>
  )
}

const SaleSummaryLineItem: React.FC<{ lineItem: LineItem }> = ({
  lineItem,
}) => {
  const variantNames = hasVariants(lineItem)
    ? getVariantsDisplayNames(lineItem)
    : ''
  const isReactSummaryPanel = getFeatureFromCache('react_summary_panel')

  return (
    <ul
      className={classNames('li-cell li-cell--readonly', {
        'vd-pt6': isReactSummaryPanel,
      })}
      data-line-item-id={lineItem.localId}
      data-adjusted={lineItem.adjusted}
    >
      <li
        className={classNames(
          'li-cell-summary-container li-cell-summary-container--readonly',
          {
            'vd-pt0 vd-pb0': isReactSummaryPanel,
          }
        )}
      >
        <div className="li-cell-summary-left-group">
          <div className="li-cell-summary li-cell-summary--quantity vd-text-label">
            {lineItem.quantity}
          </div>
          <div className="li-cell-summary li-cell-summary--name-container">
            <div className="li-cell-summary pro-li-cell-summary-name vd-text-label">
              {lineItem.product.name}
              {variantNames && (
                <div className="vd-text-supplementary">{variantNames}</div>
              )}
              {lineItem.giftCardNumber && (
                <div className="vd-text-supplementary">
                  Card Number: {lineItem.giftCardNumber}
                </div>
              )}

              {lineItem.promotionDetails && (
                <LineItemPromotionDetails
                  promotionDetails={lineItem.promotionDetails}
                />
              )}

              <LineItemCustomFields lineItem={lineItem} />
            </div>
          </div>
        </div>
        <div className="li-cell-summary-right-group">
          <div className="li-cell-summary li-cell-summary--totals">
            <div className="li-cell-summary--total-price">
              <LineItemTotalPrice totals={lineItem.totals} />
            </div>
            {isPriceDiscounted(lineItem) && (
              <div
                className="li-cell-summary--total-base-price"
                data-cy="line-item-total-base-price"
              >
                <LineItemTotalBasePrice totals={lineItem.totals} />
              </div>
            )}
          </div>
        </div>
      </li>
      {lineItem.note && (
        <li
          className={classNames(
            'li-cell-summary-container-notes vd-text-supplementary',
            {
              'vd-pt4': isReactSummaryPanel,
            }
          )}
        >
          <div className="wr-honour-line-breaks">Note: {lineItem.note}</div>
        </li>
      )}
    </ul>
  )
}

export const SaleSummaryLineItemsComponent = reactular(
  SaleSummaryLineItems,
  [],
  'reactWrapper'
)
